import React, { useCallback, useEffect, useRef, useState } from 'react';
import t from 'react-translate';
import moment from 'moment';
import { useAppDispatch } from 'redux/store';
import { isEmpty, pick, reduce, values } from 'underscore';
import { LiveEventSession, RegisteredSessionParams } from 'redux/schemas/models/live-event';
import { getRegisteredSessionDetails } from 'redux/actions/live-events';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { downloadCSV } from 'shared/csv-utils';
import ClickableContainer from 'components/clickable-container';
import useInfiniteScroll from 'shared/hooks/use-infinite-scroll';
import usePrevious from 'shared/hooks/use-previous';
import UserListModalBody, { UserListEntry } from 'shared/components/user-list-modal-body';

type RegisteredUser = UserListEntry & {
  firstName: string;
  lastName: string;
};

type RegisteredDetailsModalProps = {
  catalogId: string;
  registeredSession: LiveEventSession;
  show: boolean;
  onClose: () => void;
};

const RegisteredDetailsModal = ({
  catalogId,
  registeredSession,
  show,
  onClose,
}: RegisteredDetailsModalProps) => {
  const [registeredUsers, setRegisteredUsers] = useState<Record<number, RegisteredUser>>({});
  const [isPageFetching, setIsPageFetching] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [onceLoaded, setOnceLoaded] = useState(false);

  const dispatch = useAppDispatch();
  const prevPageNo = usePrevious(pageNo);

  const hasMore = registeredSession?.totalRegisteredUsersCount > values(registeredUsers)?.length;

  const userTable = useRef<HTMLDivElement>(null);
  const isScrolledToBottom = useInfiniteScroll(userTable.current, 0);

  const fetchRegisteredUSers = useCallback(async (fetchAll?: boolean) => {
    const params: RegisteredSessionParams = {
      catalogId,
      liveSessionId: registeredSession.id,
    };
    if (!fetchAll) {
      params.page = pageNo;
    }
    const data = await dispatch(getRegisteredSessionDetails(params))
      .then(result => {
        const userData = reduce(
          result?.payload?.registeredUsers,
          (users: Record<number, RegisteredUser>, user: RegisteredUser) => {
            users[user.id] = pick(user, ['id', 'fullName', 'email', 'firstName', 'lastName']);
            return users;
          },
          {} as Record<number, RegisteredUser>,
        );
        if (!fetchAll) {
          setOnceLoaded(true);
          setRegisteredUsers({ ...registeredUsers, ...userData });
          setIsPageFetching(false);
          return null;
        }
        return userData;
      });
    return data;
  }, [catalogId, dispatch, pageNo, registeredSession.id, registeredUsers]);

  useEffect(() => {
    if (show
      && isEmpty(registeredUsers)
      && !onceLoaded
      && !isPageFetching
    ) {
      setIsPageFetching(true);
      fetchRegisteredUSers();
    }
  }, [fetchRegisteredUSers, registeredUsers, onceLoaded, show, isPageFetching]);

  useEffect(() => {
    if (userTable && isScrolledToBottom && !isPageFetching && hasMore) {
      setPageNo(prev => prev + 1);
    }
  }, [hasMore, isPageFetching, isScrolledToBottom]);

  useEffect(() => {
    if (show
      && pageNo > 1
      && pageNo !== prevPageNo
      && hasMore
      && !isPageFetching
    ) {
      setIsPageFetching(true);
      fetchRegisteredUSers();
    }
  }, [fetchRegisteredUSers, hasMore, pageNo, prevPageNo, show, isPageFetching]);

  const downloadAttendeesCSV = useCallback(async () => {
    setIsDownloading(true);
    const registeredUsersData = values(registeredUsers)?.length === registeredSession?.totalRegisteredUsersCount
      ? registeredUsers : await fetchRegisteredUSers(true);
    if (registeredUsersData) {
      const headers = ['First Name', 'Last Name', 'Email'];
      const data = [headers];
      const usersData = values(registeredUsersData).map(({ firstName, lastName, email }) => [firstName, lastName, email]);
      data.push(...usersData);

      const timestamp = (new Date()).toISOString().replace(/[^A-Z0-9]/g, '');
      const csvName = `${registeredSession.title}_${catalogId}_${timestamp}.csv`;

      downloadCSV(data, csvName);
      setTimeout(() => {
        setIsDownloading(false);
      }, 300);
    }
  }, [
    catalogId, fetchRegisteredUSers, registeredSession?.title,
    registeredSession?.totalRegisteredUsersCount, registeredUsers,
  ]);

  const downloadAction = !isEmpty(registeredUsers) ? (
    <ClickableContainer
      className={`ml-1 text-primary ${isDownloading ? 'disabled' : ''}`}
      onClick={downloadAttendeesCSV}
      disabled={isDownloading}
    >
      ({isDownloading ? t.FILE_UPLOAD.DOWNLOADING() : t.FILE_UPLOAD.DOWNLOAD()})
    </ClickableContainer>
  ) : undefined;

  return (
    <NvModal
      show={show}
      type={ModalType.DYNAMIC}
      header={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.REGISTRATION.TITLE()}
      body={(
        <UserListModalBody
          title={registeredSession?.title}
          subtitle={moment(registeredSession?.startTime).format('LLL')}
          countLabel={t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.REGISTRATION.COUNT(registeredSession?.totalRegisteredUsersCount)}
          users={registeredUsers}
          isLoaded={onceLoaded}
          isPageFetching={isPageFetching}
          userTableRef={userTable}
          downloadAction={downloadAction}
        />
      )}
      onClose={onClose}
      fullHeight={false}
    />
  );
};

export default RegisteredDetailsModal;
